import { tagsApi } from "api/instances";
import { GetTopicFilters, TopicDetails, TopicErrorMessage, TopicGroup, TopicsState } from "./models";
import { Action, PayloadAction, createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { getDeleteMessage, getStateUpdateMessage } from "./utils/topicTextGenerator";

const defaultState: TopicsState = {
    topicGroups: [],
    isFetching: false,
    isUpdating: false,
    successMessage: "",
    errorMessage: ""
};

interface ISetMsgAction extends Action {
    payload: string;
}

interface IDeletionPayload {
    topics: string[];
    topicGroups: string[];
}

interface IEditGroupPayload {
    id: string;
    name: string;
}

interface IEditStatePayload {
    topics: string[];
    disable: boolean;
}

interface IEditTopicPayload {
    id: string;
    input: TopicDetails;
}

interface IMoveTopicsPayload {
    groupId: string;
    topics: string[];
}

class TopicNameError extends Error {
    originalError: AxiosError;
    status?: number;
    responseData?: any;

    constructor(message: string, originalError: AxiosError) {
        super(message);
        this.name = "TopicNameError";
        this.originalError = originalError;
        this.status = originalError.response?.status;
        this.responseData = originalError.response?.data;
    }
}

const handleBadRequests = (err: AxiosError, thunkApi, errorMessage: TopicErrorMessage) => {
    if (!err.isAxiosError) throw err;

    const status = err.response?.status;
    if (status === 400) {
        thunkApi.dispatch({ type: topicSlice.actions.SET_ERR_MSG, payload: errorMessage });
        return new TopicNameError(errorMessage, err);
    }

    if (status === 500) {
        thunkApi.dispatch({ type: topicSlice.actions.SET_ERR_MSG, payload: errorMessage });
        return new TopicNameError(errorMessage, err);
    }

    return new TopicNameError("", err);
}

const isSuccessful = (s? : number) => s === 200;

export const createTopic = createAsyncThunk("CREATE_TOPIC", async (input: TopicDetails, thunkApi) => {
    const res = await tagsApi.createTopic(input)
        .catch((err: AxiosError<any>) => {
            throw handleBadRequests(err, thunkApi, TopicErrorMessage.Topic);
        });
    return isSuccessful(res?.status);
});

export const createTopicGroup = createAsyncThunk("CREATE_TOPIC_GROUP", async (name: string, thunkApi) => {
    const res = await tagsApi.createTopicGroup(name)
        .catch((err: AxiosError<any>) => {
            throw handleBadRequests(err, thunkApi, TopicErrorMessage.TopicGroup);
        });
    return isSuccessful(res?.status);
});

export const editTopic = createAsyncThunk("EDIT_TOPIC", async ({ id, input }: IEditTopicPayload, thunkApi) => {
    const res = await tagsApi.editTopic(id, input)
        .catch((err: AxiosError) => {
            throw handleBadRequests(err, thunkApi, TopicErrorMessage.Topic)
        });
    return isSuccessful(res?.status)
});

export const editTopicGroup = createAsyncThunk("EDIT_TOPIC_GROUP", async ({ id, name }: IEditGroupPayload, thunkApi) => {
    const res = await tagsApi.editTopicGroup(id, name)
        .catch((err: AxiosError) => {
            throw handleBadRequests(err, thunkApi, TopicErrorMessage.TopicGroup);
        });
    return isSuccessful(res?.status)
});

export const editTopicState = createAsyncThunk("EDIT_TOPIC_STATE", async ({ topics, disable }: IEditStatePayload, thunkApi) => {
    try {
        await tagsApi.editTopicState(topics, disable)
        thunkApi.dispatch({ type: topicSlice.actions.SET_SUCCESS_MSG, payload: getStateUpdateMessage(topics.length, disable) });
    }
    catch (err) {
        throw err;
    }
});

export const moveTopics = createAsyncThunk("MOVE_TOPICS", async ({ topics, groupId }: IMoveTopicsPayload, thunkApi) => {
    const res = await tagsApi.moveTopics(topics, groupId)
        .then((_: any) => {
            thunkApi.dispatch({
                type: topicSlice.actions.SET_SUCCESS_MSG,
                payload: "Successfully moved to new topic group."
            });
            return {status: 200};
        })
        .catch((err: AxiosError) => {
            throw handleBadRequests(err, thunkApi, TopicErrorMessage.Move)
        });
    return isSuccessful(res?.status);
});

export const deleteTopicsAndGroups = createAsyncThunk("DELETE_TOPICS_AND_GROUPS", async ({ topics, topicGroups }: IDeletionPayload, thunkApi) => {
    try {
        await tagsApi.deleteTopicsAndGroups(topics, topicGroups);
        thunkApi.dispatch({ type: topicSlice.actions.SET_SUCCESS_MSG, payload: getDeleteMessage(topics.length, topicGroups.length) });
    } catch (err) {
        throw err;
    }
});

export const getTopicGroups = createAsyncThunk("GET_TOPIC_GROUPS", async (filters: GetTopicFilters | undefined, thunkApi) => {
    try {
        return await tagsApi.searchTopics(filters);
    } catch (err) {
        throw err;
    }
});

const rejectedUpdateMatcher = isRejected(
    editTopicState,
    deleteTopicsAndGroups,
    getTopicGroups
);

const fulfilledUpdateMatcher = isFulfilled(
    editTopicState,
    deleteTopicsAndGroups,
    getTopicGroups
);

const pendingUpdateMatcher = isPending(
    editTopicState,
    deleteTopicsAndGroups
);

const successfulDialogOperation = isFulfilled(
    editTopicGroup,
    editTopic,
    createTopic,
    createTopicGroup,
    moveTopics
);

const pendingUpdateErrorMatcher = isPending(
    createTopicGroup,
    createTopic,
    editTopicGroup,
    editTopic,
    moveTopics,
);

const pendingUpdateSuccessMatcher = isPending(
    moveTopics,
    editTopicState,
    deleteTopicsAndGroups
);

export const topicSlice = createSlice({
    name: "topics",
    initialState: defaultState,
    reducers: {
        SET_ERR_MSG: (state: TopicsState, action: ISetMsgAction) => {
            state.errorMessage = action.payload;
        },
        SET_SUCCESS_MSG: (state: TopicsState, action: ISetMsgAction) => {
            state.successMessage = action.payload;
        }
    },
    extraReducers: (builder) => builder
        .addCase(getTopicGroups.pending, (state: TopicsState) => {
            state.isFetching = true;
        })
        .addCase(getTopicGroups.fulfilled, (state: TopicsState, action: PayloadAction<TopicGroup[]>) => {
            state.isFetching = false;
            state.topicGroups = action.payload;
        })
        .addCase(getTopicGroups.rejected, (state: TopicsState) => {
            state.isFetching = false;
            state.topicGroups = [];
        })
        .addMatcher(pendingUpdateErrorMatcher, (state, _) => { state.errorMessage = "" })
        .addMatcher(pendingUpdateSuccessMatcher, (state, _) => { state.successMessage = "" })
        .addMatcher(
            (action) => pendingUpdateMatcher(action) || successfulDialogOperation(action),
            (state, _) => { state.isUpdating = true })
        .addMatcher(fulfilledUpdateMatcher, (state, _) => {
            state.isUpdating = false
            if (state.errorMessage !== "") state.errorMessage = "";
        })
        .addMatcher(rejectedUpdateMatcher, (state, _) => {
            state.isUpdating = false;
            if (state.successMessage !== "") state.successMessage = "";
        })
});